//dependency
import React, { memo, ReactElement, RefObject, useCallback, useState, useMemo, useContext, useEffect } from "react";

//interface
import { IDeliverySlot, IDeliverySlots } from "@swiggy-private/cart-types";

//helpers
import { TEXTS } from "@cart-ui/constants";
import { isEmpty, useEffectOnce, format } from "@swiggy-private/core-ui";
import { convertToDisplaySlot } from "./utils";

//styles
import styles from "./SlotBottomsheet.scss";

//components
import { CartPaymentFooter } from "@cart-ui/components/cart-payment-footer";
import { Slot } from "./slot";
import { Overlay } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";

//test-ids
import { SLOT_TEST_IDS } from "@swiggy-private/cart-test-ids";

// icons
import PlusIcon from "@cart-ui/widgets/plus-icon/PlusIcon";

// constants
import { SHOW_DELIVERY_SLOT_CARDS, TEXTS as BOTTOM_SHEET_TEXTS } from "@cart-ui/constants/SlotBottomsheet";
import { UX4_PRIMARY_ORANGE } from "@cart-ui/constants/Color";

// contexts
import { DeliveryInfoContext } from "@swiggy-private/checkout-core";

interface ISlotBottomsheet {
    viewDetailedBill: VoidFunction;
    makePayment: (selectedSlot: string) => void;
    cartTotal: number;
    tipAmount?: number;
    makePaymentButtonRef?: RefObject<HTMLDivElement>;
    canPlaceOrderFromCart?: boolean;
    showAddressWidgetAtTop?: boolean;
    deliverySlots?: IDeliverySlots;
    setShowSlots: Function;
    onSlotSelect: Function;
    selectDeliverySlot: Function;
    analyticsEvents: {
        impressionProceedToPay: Function;
        impressionSelectDeliverySlotButton: Function;
        impressionAllDelierySlots: Function;
    };
    updateCartInProgress: boolean;
    isBulkOrder: boolean;
}

const SlotBottomsheet = ({
    deliverySlots,
    cartTotal,
    makePayment,
    viewDetailedBill,
    tipAmount,
    makePaymentButtonRef,
    canPlaceOrderFromCart,
    setShowSlots,
    onSlotSelect,
    selectDeliverySlot,
    analyticsEvents,
    updateCartInProgress,
    isBulkOrder,
}: ISlotBottomsheet): ReactElement | null => {
    const { setDeliverySelectedSlot } = useContext(DeliveryInfoContext);

    const [showDeliverySlots, setShowDeliverySlots] = useState<IDeliverySlot[]>();

    const expandSlotBottomsheet = useMemo(() => {
        if (!deliverySlots || !deliverySlots.slots) {
            return false;
        }
        const selectedSlotIndex = deliverySlots?.slots?.findIndex(val => val?.selected === true);

        return selectedSlotIndex >= SHOW_DELIVERY_SLOT_CARDS.MINIMUM_CARDS;
    }, [deliverySlots]);

    const getSelectedSlot = useMemo(() => {
        // by default only select a slot when it is coming selected from backend
        const selectedSlot = deliverySlots?.slots.filter(slot => slot?.selected === true);
        if (!isEmpty(selectedSlot)) {
            return convertToDisplaySlot(selectedSlot[0].slot_start_time.seconds, selectedSlot[0].slot_end_time.seconds);
        }

        return "";
    }, [deliverySlots]);

    const [selectedSlot, setSelectedSlot] = useState(getSelectedSlot);

    useEffect(() => {
        if (!deliverySlots || !deliverySlots.slots) {
            return setShowDeliverySlots([]);
        }
        // show only 6 delivery slots initially, if total slots are more than 8
        else if (deliverySlots.slots.length > SHOW_DELIVERY_SLOT_CARDS.THRESHOLD) {
            if (expandSlotBottomsheet) {
                return setShowDeliverySlots(deliverySlots.slots);
            }
            return setShowDeliverySlots(deliverySlots.slots.slice(0, SHOW_DELIVERY_SLOT_CARDS.MINIMUM_CARDS));
        } else {
            setShowDeliverySlots(deliverySlots.slots);
        }
    }, [deliverySlots, expandSlotBottomsheet]);

    const onClose = useCallback(() => {
        setShowSlots(false);
    }, [setShowSlots]);

    const showAllSlots = useCallback(() => {
        setShowDeliverySlots(deliverySlots?.slots || []);
    }, [deliverySlots?.slots]);

    const showViewMoreSlots = useMemo(() => {
        return deliverySlots?.slots.length !== showDeliverySlots?.length;
    }, [deliverySlots?.slots.length, showDeliverySlots?.length]);

    useEffectOnce(() => {
        analyticsEvents.impressionAllDelierySlots();
    });

    const makePaymentFunc = useCallback(() => {
        if (isEmpty(selectedSlot) || isEmpty(deliverySlots?.date)) return;
        setDeliverySelectedSlot({
            deliveryTimeAnnotation: deliverySlots?.date ?? "",
            deliveryTime: selectedSlot ?? "",
        });
        makePayment(selectedSlot);
    }, [makePayment, deliverySlots, selectedSlot, setDeliverySelectedSlot]);

    const getClassNames = useMemo(() => {
        return {
            container: styles.cartPaymentFooter,
            secondaryButton: !selectedSlot ? styles.disabledButton : undefined,
        };
    }, [selectedSlot]);

    if (isEmpty(deliverySlots)) {
        return null;
    }

    return (
        <HalfCard isOpen closeOnOverlayClick onClose={onClose} hideCloseIcon={true}>
            <>
                {updateCartInProgress && <Overlay visible={true} className={styles.overlay} />}
                <div className={styles.wrapper}>
                    <div className={styles.slotInfoWrapper}>
                        <div className={styles.titleWrapper}>
                            <div data-testid={SLOT_TEST_IDS.SLOT_BOTTOMSHEET_TITLE} className={styles.title}>
                                {TEXTS.SLOT_TTILE}
                            </div>
                            <div data-testid={SLOT_TEST_IDS.SLOT_BOTTOMSHEET_SUBTITLE} className={styles.subtitle}>
                                {isBulkOrder
                                    ? format(TEXTS.BULK_ORDER_SLOT_SUBTITLE, deliverySlots.date)
                                    : format(TEXTS.SLOT_SUBTITLE, deliverySlots.date)}
                            </div>
                        </div>
                        <div data-testid={SLOT_TEST_IDS.SLOTS_LIST} className={styles.slotWrapper}>
                            {showDeliverySlots?.map((slot: IDeliverySlot) => (
                                <Slot
                                    key={slot.slot_id}
                                    slot={slot}
                                    onSlotSelect={onSlotSelect}
                                    setSelectedSlot={setSelectedSlot}
                                />
                            ))}
                        </div>
                        {showViewMoreSlots && showDeliverySlots && (
                            <div className={styles.remainingSlotQtyWrapper} onClick={showAllSlots}>
                                <div className={styles.remainingSlotQty}>
                                    <PlusIcon iconFillColor={UX4_PRIMARY_ORANGE} />{" "}
                                    <Text
                                        className={styles.remainingSlotQtyNumber}
                                        variant="V2.body2.neutral.bold"
                                        color="primary"
                                    >
                                        {deliverySlots.slots?.length - showDeliverySlots?.length}
                                    </Text>
                                </div>
                                <Text variant="V2.body1.neutral.bold" color="primary">
                                    {BOTTOM_SHEET_TEXTS.VIEW_MORE_SLOTS}
                                </Text>
                            </div>
                        )}
                    </div>
                    <CartPaymentFooter
                        cartTotal={cartTotal}
                        tipAmount={tipAmount}
                        makePayment={makePaymentFunc}
                        viewDetailedBill={viewDetailedBill}
                        canPlaceOrderFromCart={canPlaceOrderFromCart}
                        makePaymentButtonRef={makePaymentButtonRef}
                        selectDeliverySlot={selectDeliverySlot}
                        impressionProceedToPay={analyticsEvents.impressionProceedToPay}
                        impressionSelectDeliverySlotButton={analyticsEvents.impressionSelectDeliverySlotButton}
                        classNames={getClassNames}
                    />
                </div>
            </>
        </HalfCard>
    );
};

export default memo(SlotBottomsheet);
